import { FilterType } from '@/lib/OrgChartFilters'
import { unref } from 'vue'
import { MappingDict } from '@/lib/mapping'
import { Types } from '@/lib/CustomFields'

const computeValues = ({
  departments,
  roles,
  managers,
  locations,
  spanOfControl,
  states,
  notMappedFields,
  workforceStatusList,
  uniqueValues,
  allCustomFieldTypes,
  rootDepth,
  rootHeight,
  sensitiveDataFields,
  sensitiveDataValues
}) => {
  const result = {}
  const notMappedFieldsValue = notMappedFields

  // Helper function to add values to result
  const addValues = (type, dataArray, prefix, skipBlankField) => {
    if (notMappedFieldsValue?.includes(MappingDict[type])) return

    const values = new Array(dataArray.length + 1)
    let index = 0

    for (let i = 0; i < dataArray.length; i++) {
      const item = dataArray[i]
      if (item) {
        values[index++] = {
          name: item,
          type: FilterType[type],
          id: `${prefix}-${item}`
        }
      }
    }

    // Add '(blanks)' option
    if (!skipBlankField)
      values[index] = { name: '(blanks)', type: FilterType[type], id: `${prefix}-blanks` }

    result[FilterType[type]] = values
  }

  addValues('Department', departments, 'department')
  addValues('Role', roles, 'role')
  addValues('Location', locations, 'location')
  addValues('SpanOfControl', spanOfControl, 'spanOfControl', true)

  // Managers
  if (!notMappedFieldsValue?.includes(MappingDict.managerId)) {
    result[FilterType.Manager] = [
      ...managers.map((manager) => ({
        name: manager.name || manager.role || manager.email || 'Manager',
        personId: manager.personId,
        type: FilterType.Manager,
        id: `manager-${manager.personId}`
      })),
      { name: '(blanks)', type: FilterType.Manager, id: 'manager-blanks' }
    ]
  }

  result[FilterType.Level] = [
    ...Array.from({ length: rootHeight }, (_, i) => i + rootDepth).map((level) => ({
      name: level,
      type: FilterType.Level,
      id: `level-${rootDepth > 1 ? level - (rootDepth - 1) : level}`
    }))
  ]

  // Scenario Status
  result[FilterType.ScenarioStatus] = [
    ...states.map((status) => ({
      name: status,
      type: FilterType.ScenarioStatus,
      id: `status-${status}`
    })),
    { name: '(blanks)', type: FilterType.ScenarioStatus, id: 'status-blanks' }
  ]

  // Position Planning
  result[FilterType.PositionPlanning] = [
    { name: 'Assigned', type: FilterType.PositionPlanning, id: 'positionPlanning-assigned' },
    {
      name: 'Unassigned',
      type: FilterType.PositionPlanning,
      id: 'positionPlanning-unassigned '
    }
  ]

  // Position Planning Scenario
  result[FilterType.PositionPlanningScenarioPositions] = [
    {
      name: 'Positions with an assignment',
      type: FilterType.PositionPlanningScenarioPositions,
      id: 'positionPlanningScenario-assigned-positions'
    },
    {
      name: 'Positions without an assignment',
      type: FilterType.PositionPlanningScenarioPositions,
      id: 'positionPlanningScenario-unassigned-positions'
    }
  ]

  // Workforce Status
  result[FilterType.WorkforceStatus] = workforceStatusList
    .map((status) => ({
      name: status,
      type: FilterType.WorkforceStatus,
      id: `workforceStatus-${status}`
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  allCustomFieldTypes.forEach(({ id: typeId, type }) => {
    const uniqueValuesForType = uniqueValues?.[typeId] || []
    const options = new Array(uniqueValuesForType.length + 1)
    let index = 0

    for (let i = 0; i < uniqueValuesForType.length; i++) {
      const fieldValue = uniqueValuesForType[i]
      if (
        fieldValue &&
        type !== Types.Succession &&
        type !== Types.Skill &&
        type !== Types.PositionShortlist
      ) {
        options[index++] = {
          name: fieldValue,
          type: FilterType.CustomFieldV2,
          id: `customFieldV2-${fieldValue}`,
          typeId
        }
      }
    }

    options[index] = {
      name: '(blanks)',
      type: FilterType.CustomFieldV2,
      id: `customFieldV2-${typeId}-blanks`,
      typeId
    }

    options.length = index + 1
    options.sort((a, b) => {
      try {
        return a.name.localeCompare(b.name)
      } catch (e) {
        console.error(e)
        return 0
      }
    })

    result[typeId] = options
  })

  // Sensitive Data
  sensitiveDataFields.forEach((sensitiveDataField) => {
    const fieldValues = sensitiveDataValues[sensitiveDataField] || []
    const options = new Array(fieldValues.length + 1)
    let index = 0

    fieldValues.forEach((field) => {
      options[index++] = {
        name: field,
        type: FilterType.SensitiveData,
        id: `sensitiveData-${field}`,
        typeId: `${FilterType.SensitiveData}_${sensitiveDataField}`
      }
    })

    options[index] = {
      name: '(blanks)',
      type: FilterType.SensitiveData,
      id: `sensitiveData-${sensitiveDataField}-blanks`,
      typeId: `${FilterType.SensitiveData}_${sensitiveDataField}`
    }

    options.sort((a, b) => a.name.localeCompare(b.name))
    result[`${FilterType.SensitiveData}_${sensitiveDataField}`] = options
  })

  return result
}

const computeCategories = ({
  computedValues,
  notMappedFields,
  fieldSettings,
  boardId,
  mainBoardId,
  allCustomFieldTypes,
  sensitiveDataFields
}) => {
  const base = []
  const notMappedFieldsValue = notMappedFields

  if (!notMappedFieldsValue?.includes(MappingDict.department)) {
    base.push({
      name: 'Department',
      type: FilterType.Department,
      id: FilterType.Department
    })
  }

  base.push({
    name: 'Level',
    type: FilterType.Level,
    id: FilterType.Level
  })

  if (!notMappedFieldsValue?.includes(MappingDict.officeLocation)) {
    base.push({
      name: 'Location',
      type: FilterType.Location,
      id: FilterType.Location
    })
  }

  if (!notMappedFieldsValue?.includes(MappingDict.managerId)) {
    base.push({
      name: 'Manager',
      type: FilterType.Manager,
      id: FilterType.Manager
    })
  }

  base.push({
    name: 'Review',
    type: FilterType.WorkforceStatus,
    id: FilterType.WorkforceStatus
  })

  if (!notMappedFieldsValue?.includes(MappingDict.role)) {
    base.push({
      name: 'Role',
      type: FilterType.Role,
      id: FilterType.Role
    })
  }

  if (unref(boardId) === mainBoardId) {
    base.push({
      name: 'Position Planning',
      type: FilterType.PositionPlanning,
      id: FilterType.PositionPlanning
    })
  } else {
    base.push({
      name: 'Positions',
      type: FilterType.PositionPlanningScenarioPositions,
      id: FilterType.PositionPlanningScenarioPositions
    })
  }

  if (unref(boardId) !== mainBoardId) {
    base.push({
      name: 'Scenario Status',
      type: FilterType.ScenarioStatus,
      id: FilterType.ScenarioStatus
    })
  }

  base.push({
    name: 'Span of Control',
    type: FilterType.SpanOfControl,
    id: FilterType.SpanOfControl
  })

  const customFieldsValue = allCustomFieldTypes
  const customFields = customFieldsValue ? new Array(customFieldsValue.length) : []

  for (let i = 0; i < customFieldsValue?.length || 0; i++) {
    const { name, id, order } = customFieldsValue[i]
    customFields[i] = {
      name,
      id,
      order: order || 0,
      type: FilterType.CustomFieldV2
    }
  }

  const sensitiveDataFieldsValue = sensitiveDataFields
  const _sensitiveDataFields = new Array(sensitiveDataFieldsValue.length)

  for (let i = 0; i < sensitiveDataFieldsValue.length; i++) {
    const field = sensitiveDataFieldsValue[i]
    _sensitiveDataFields[i] = {
      name: field,
      type: FilterType.SensitiveData,
      id: `${FilterType.SensitiveData}_${field}`
    }
  }

  const categories = new Array(base.length + customFields.length + _sensitiveDataFields.length)
  let index = 0

  for (let i = 0; i < base.length; i++) {
    categories[index++] = base[i]
  }
  for (let i = 0; i < customFields.length; i++) {
    categories[index++] = customFields[i]
  }
  for (let i = 0; i < _sensitiveDataFields.length; i++) {
    categories[index++] = _sensitiveDataFields[i]
  }

  const unfilteredCats = [].concat(categories).sort((a, b) => {
    if (a.order === 1) return -1
    if (b.order === 1) return 1
    if (a.order && b.order) return a.order - b.order
    if (a.order) return -1
    if (b.order) return 1
    return a.name.localeCompare(b.name)
  })

  const filteredCats = unfilteredCats.filter((category) => {
    const categoryValues = computedValues[category.id]
    const hasFilterValues = categoryValues && categoryValues.length > 0
    const isFilterEnabled = fieldSettings[category.id] ?? true

    return hasFilterValues && isFilterEnabled
  })

  return { unfilteredCategories: unfilteredCats, categories: filteredCats }
}

onmessage = ({ data }) => {
  const newValues = computeValues({
    departments: data.departments,
    roles: data.roles,
    managers: data.managers,
    locations: data.locations,
    spanOfControl: data.spanOfControl,
    states: data.states,
    notMappedFields: data.notMappedFields,
    workforceStatusList: data.workforceStatusList,
    uniqueValues: data.uniqueValues,
    allCustomFieldTypes: data.allCustomFieldTypes,
    fieldSettings: data.fieldSettings,
    sensitiveDataFields: data.sensitiveDataFields,
    sensitiveDataValues: data.sensitiveDataValues,
    rootDepth: data.rootDepth,
    rootHeight: data.rootHeight
  })

  const { categories: newCategories, unfilteredCategories: newUnfilteredCategories } =
    computeCategories({
      computedValues: newValues,
      notMappedFields: data.notMappedFields,
      fieldSettings: data.fieldSettings,
      boardId: data.boardId,
      mainBoardId: data.mainBoardId,
      allCustomFieldTypes: data.allCustomFieldTypes,
      sensitiveDataFields: data.sensitiveDataFields
    })

  postMessage({
    newValues,
    newCategories,
    newUnfilteredCategories
  })
}
